import './defines';
import {
	arrow as arrowMiddleware,
	autoUpdate,
	computePosition,
	flip,
	offset,
	type Placement,
	shift,
	size,
} from '@floating-ui/dom';
import type {
	TooltipMainPositionSide,
	TooltipPosition,
	TooltipSecondaryPositionSide,
} from 'interfaces/app';
import { ServiceEvent } from 'services/service-event';
import { Public } from 'utils/decorators';
import ClickOutside from 'utils/v-click-outside';
import { getInstanceAPI } from 'utils/vue';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import type {
	ComponentPublicInstance,
	CSSProperties,
	HTMLAttributes,
} from 'vue';
import {
	Prop,
	Ref,
	Vue,
	type VueExtended,
	Watch,
} from 'vue-facing-decorator';
import type { Cons } from 'vue-facing-decorator/dist/component';
import Template from './template.vue';

@Component({
	name: 'TooltipComponent',
	directives: {
		ClickOutside: ClickOutside.directive,
	},
	emits: [
		'click:outside',
		'close',
	],
	mixins: [Template],
})
class TooltipComponent<BodyComponent extends Cons> extends (Vue as typeof VueExtended) {
	@Prop({
		default: undefined,
		description: 'Defines the anchor element of the tooltip, if not defined the anchor will be the parent element',
		type: [
			HTMLElement,
			Object,
		],
	})
	public readonly anchor?: HTMLElement | ComponentPublicInstance;

	@Prop({
		default: undefined,
		description: 'Defines the function that will be called before the tooltip is closed with the ability to prevent the close action',
		type: Function,
	})
	public readonly beforeClose?: ServiceEventHandler<any>;

	@Prop({
		default: undefined,
		description: "Defines the body component's options of the tooltip",
		type: Object,
	})
	public readonly body?: TooltipServiceOptionsBody<BodyComponent>;

	@Prop({
		default: () => ({}),
		description: 'Defines the CSS classes for the tooltip body',
		type: [
			Array,
			Object,
		],
	})
	public readonly bodyClasses!: string[] | Record<string, boolean>;

	@Prop({
		default: () => ({}),
		description: 'Defines the CSS styles for the tooltip body',
		type: Object,
	})
	public readonly bodyStyles!: Partial<CSSStyleDeclaration> & Record<string, string>;

	@Prop({
		default: 8,
		description: 'Defines the border radius of the tooltip',
		type: [
			Number,
			String,
		],
	})
	public readonly borderRadius?: number | string;

	@Prop({
		default: false,
		description: 'Indicates if the tooltip should be closed when the user clicks outside of it',
		type: Boolean,
	})
	public readonly closeOnModalClick!: boolean;

	@Prop({
		description: 'Defines the distance between the anchor and the tooltip',
		required: true,
		type: [
			Number,
			String,
		],
	})
	public readonly distance!: number | string;

	@Prop({
		default: true,
		description: 'Indicates if the tooltip have a close button',
		type: Boolean,
	})
	public readonly hasCloseButton!: boolean;

	@Prop({
		acceptedValues: [
			'bottom center',
			'bottom left',
			'bottom right',
			'right',
			'top center',
			'top left',
			'top right',
			'left',
		],
		default: 'bottom center',
		schema: 'TooltipPosition',
		type: String,
	})
	public readonly initialPosition!: TooltipPosition;

	@Prop({
		default: true,
		description: 'Indicates if the tooltip should shown as a modal',
		type: Boolean,
	})
	public readonly isModal!: boolean;

	@Prop({
		default: () => ({}),
		description: 'Defines the listeners for the tooltip body',
		type: Object,
	})
	public readonly listeners!: Record<string, ServiceEventHandler<any>[] | ServiceEventHandler<any>>;

	@Prop({
		default: false,
		description: "Indicates if the tooltip's top arrow should not be shown",
		type: Boolean,
	})
	public readonly noArrow!: boolean;

	@Prop({
		default: false,
		description: 'Indicates if the tooltip should not have any styles',
		type: Boolean,
	})
	public readonly noStyles!: boolean;

	@Prop({
		default: undefined,
		type: [
			Array,
			Object,
			String,
		],
	})
	public readonly positionerClasses?: HTMLAttributes['class'];

	@Prop({
		default: undefined,
		type: [
			Array,
			Object,
			String,
		],
	})
	public readonly positionerStyles?: HTMLAttributes['style'];

	@Prop({
		default: false,
		description: 'Indicates if the tooltip should be teleported using the same logic as the `openTooltip` service',
		type: Boolean,
	})
	public readonly teleport?: boolean;

	@Prop({
		default: undefined,
		description: 'Defines the title of the tooltip',
		type: String,
	})
	public readonly title?: string;

	@Prop({
		default: () => ({}),
		description: 'Defines the CSS classes for the tooltip element',
		type: [
			Array,
			Object,
			String,
		],
	})
	public readonly tooltipClasses!: string[] | string | Record<string, boolean>;

	@Prop({
		default: () => ({}),
		description: 'Defines the CSS classes for the tooltip container element',
		type: [
			Array,
			Object,
		],
	})
	public readonly tooltipContainerClasses!: string[] | Record<string, boolean>;

	@Prop({
		default: () => ({}),
		description: 'Defines the CSS styles for the tooltip element',
		type: Object,
	})
	public readonly tooltipStyles!: CSSProperties;

	protected get bodyComponentInstanceAPI(): Record<string, any> | undefined {
		const defaultSlotComponentInstance = this.$getSlot<InstanceType<BodyComponent>>('default');

		if (defaultSlotComponentInstance) {
			return getInstanceAPI(defaultSlotComponentInstance);
		}

		return undefined;
	}

	protected get computedAnchor(): HTMLElement | undefined {
		if (!this.anchor) {
			return this.$el?.parentElement;
		}

		if ('_isVueClass' in this.anchor) {
			return this.anchor.$el as HTMLElement;
		}

		return this.anchor as HTMLElement;
	}

	protected get computedTooltipContainerClasses(): Record<string, boolean> {
		const classes: Record<string, boolean> = {
			'tooltip-modal': this.isModal,
			[this.internalTheme]: true,
			...this.manualTooltipContainerClasses,
		};

		if (Array.isArray(this.tooltipContainerClasses)) {
			for (const className of this.tooltipContainerClasses) {
				classes[className] = true;
			}
		} else {
			Object.assign(
				classes,
				this.tooltipContainerClasses,
			);
		}

		return classes;
	}

	protected get computedTooltipHeaderClasses(): Record<string, boolean> {
		return {
			'tooltip-header-has-title': !!this.title,
		};
	}

	protected get computedTooltipPositionerClasses(): HTMLAttributes['class'] {
		return this.positionerClasses;
	}

	protected get computedTooltipPositionerStyles(): HTMLAttributes['style'] {
		const styles: HTMLAttributes['style'] = [];

		if (Array.isArray(this.positionerStyles)) {
			styles.push(...this.positionerStyles);
		} else if (this.positionerStyles) {
			styles.push(this.positionerStyles);
		}

		const tooltipStylesKeys = Object.keys(this.tooltipStyles) as Array<keyof CSSProperties>;

		for (const prop of tooltipStylesKeys) {
			if (
				prop.startsWith('--')
				|| prop.startsWith('var(')
			) {
				styles.push(`${prop}: ${this.tooltipStyles[prop]}`);
			}
		}

		styles.push(this.tooltipPositionerStyles);

		return styles;
	}

	protected get computedTooltipWrapperClasses(): HTMLAttributes['class'] {
		const classes: HTMLAttributes['class'] = [
			{
				'tooltip-wrapper-no-arrow': this.noArrow,
				'tooltip-wrapper-no-styles': this.noStyles,
			},
		];

		if (Array.isArray(this.tooltipClasses)) {
			classes.push(...this.tooltipClasses);
		} else {
			classes.push(this.tooltipClasses);
		}

		return classes;
	}

	protected get computedTooltipWrapperStyles(): CSSProperties {
		const styles: CSSProperties = {};

		if (!this.noStyles) {
			if (typeof this.borderRadius === 'number') {
				styles.borderRadius = `${this.borderRadius}px`;
			} else {
				styles.borderRadius = this.borderRadius;
			}
		}

		return {
			...styles,
			...(Object
				.keys(this.tooltipStyles) as Array<keyof CSSProperties>)
				.reduce(
					(tooltipStyles, prop) => {
						if (
							!prop.startsWith('--')
							&& !prop.startsWith('var(')
						) {
							(tooltipStyles as any)[prop] = this.tooltipStyles[prop];
						}

						return tooltipStyles;
					},
					{} as CSSProperties,
				),
		};
	}

	protected get hasHeader(): boolean {
		return !!(
			this.title
			|| this.hasCloseButton
		);
	}

	protected get teleportElement(): HTMLElement | null {
		const dialogsElement = document.getElementById('dialogs');
		const webAppElement = document.getElementById('webapp');

		return (
			dialogsElement?.parentElement
			?? webAppElement
		);
	}

	@Ref('tooltipArrow')
	private tooltipArrowElement?: HTMLDivElement;

	@Ref('tooltipBody')
	private tooltipBodyElement?: HTMLDivElement;

	@Ref('tooltipHeader')
	private tooltipHeaderElement?: HTMLDivElement;

	@Ref('tooltipPositioner')
	private tooltipPositionerElement?: HTMLDivElement;

	@Ref('tooltipWrapper')
	private tooltipWrapperElement?: HTMLDivElement;

	private isVisible = false;

	private manualTooltipContainerClasses: Record<string, boolean> = {
		invisible: true,
	};

	private tooltipFloatingUICleanup?: () => void;

	private tooltipMainPositionSide: TooltipMainPositionSide | null = null;

	private tooltipPositionerStyles: CSSProperties = {};

	private tooltipSecondaryPositionSide: TooltipSecondaryPositionSide | null = null;

	protected beforeUnmount(): void {
		document.body.classList.remove('tooltip-modal-no-scroll');
		document.documentElement.classList.remove('tooltip-modal-no-scroll');
		this.tooltipFloatingUICleanup?.();
	}

	protected mounted(): void {
		this.$forceCompute('computedAnchor');
	}

	@Watch(
		'initialPosition',
		{
			immediate: true,
		},
	)
	protected onInitialPositionChange(): void {
		if (this.initialPosition.startsWith('bottom')) {
			this.tooltipMainPositionSide = 'bottom';
		} else if (this.initialPosition.startsWith('right')) {
			this.tooltipMainPositionSide = 'right';
		} else if (this.initialPosition.startsWith('top')) {
			this.tooltipMainPositionSide = 'top';
		} else if (this.initialPosition.startsWith('left')) {
			this.tooltipMainPositionSide = 'left';
		}

		if (this.initialPosition.endsWith('center')) {
			this.tooltipSecondaryPositionSide = 'center';
		} else if (this.initialPosition.endsWith('right')) {
			this.tooltipSecondaryPositionSide = 'right';
		} else if (this.initialPosition.endsWith('left')) {
			this.tooltipSecondaryPositionSide = 'left';
		} else if (this.initialPosition.endsWith('bottom')) {
			this.tooltipSecondaryPositionSide = 'bottom';
		} else if (this.initialPosition.endsWith('top')) {
			this.tooltipSecondaryPositionSide = 'top';
		}
	}

	@Watch('isVisible')
	protected onIsVisibleChange(): void {
		const {
			computedAnchor,
			tooltipPositionerElement,
		} = this;

		if (!this.isVisible) {
			this.manualTooltipContainerClasses.invisible = true;
		}

		if (
			this.isVisible
			&& computedAnchor
			&& tooltipPositionerElement
		) {
			this.$nextTick(() => {
				this.tooltipFloatingUICleanup = autoUpdate(
					computedAnchor,
					tooltipPositionerElement,
					() => this.computePosition(),
					{
						animationFrame: !!this.teleport,
					},
				);
				this.computePosition();
			});
		} else {
			this.tooltipFloatingUICleanup?.();
			this.tooltipFloatingUICleanup = undefined;
		}
	}

	@Public()
	public bodyComponent(): (Record<string, any>) | undefined {
		return this.bodyComponentInstanceAPI as (Record<string, any>) | undefined;
	}

	@Public()
	public bodyElement(): Decorated<HTMLDivElement> {
		return this.tooltipBodyElement as Decorated<HTMLDivElement>;
	}

	private async computePosition(): Promise<void> {
		if (
			!this.computedAnchor
			|| !this.isVisible
			|| !this.tooltipPositionerElement
			|| !this.tooltipWrapperElement
		) {
			return;
		}

		const { tooltipWrapperElement } = this;
		let initialPlacement: Placement = 'bottom';

		if (
			this.tooltipMainPositionSide
			&& this.tooltipSecondaryPositionSide === 'center'
		) {
			initialPlacement = this.tooltipMainPositionSide;
		} else if (
			this.tooltipMainPositionSide
			&& this.tooltipSecondaryPositionSide
		) {
			initialPlacement = `${this.tooltipMainPositionSide}-${this.tooltipSecondaryPositionSide
				.replace(
					/(left|top)/,
					'start',
				)
				.replace(
					/(right|bottom)/,
					'end',
				) as 'start' | 'end'}`;
		}

		const {
			x,
			y,
			middlewareData,
			placement,
		} = await computePosition(
			this.computedAnchor,
			this.tooltipPositionerElement,
			{
				placement: initialPlacement,
				middleware: [
					/**
					 * Translates the floating element along the specified axes.
					 *
					 * This lets you add distance (margin or spacing) between the reference
					 * and floating element, slightly alter the placement, or even create
					 * custom placements.
					 */
					offset(({ rects }) => {
						let { distance } = this;

						if (typeof distance === 'string') {
							if (distance.startsWith('--')) {
								const rootElementOrParent: HTMLElement = (
									(
										!this.teleport
										&& this.$el
									)
									|| this.$el.parentElement
								);

								if (rootElementOrParent) {
									distance = getComputedStyle(rootElementOrParent).getPropertyValue(distance);
									distance = parseInt(
										distance,
										10,
									);
								}
							} else {
								distance = parseInt(
									distance,
									10,
								);
							}

							if (
								typeof distance === 'string'
								|| Number.isNaN(distance)
							) {
								distance = 0;
							}
						}

						if (distance < 0) {
							distance -= rects.floating.height;
						}

						return distance;
					}),
					/**
					 * Changes the placement of the floating element to keep it in view.
					 *
					 * This prevents the floating element from overflowing along its
					 * side axis by flipping it to the opposite side by default.
					 */
					flip(),
					/**
					 * Shifts the floating element to keep it in view.
					 *
					 * This prevents the floating element from overflowing along its
					 * axis of alignment, thereby preserving the side it’s placed on.
					 */
					shift(),
					/**
					 * Provides data to change the size of a floating element.
					 *
					 * This is useful to ensure the floating element isn’t too big to
					 * fit in the viewport (or more specifically, its clipping context),
					 * especially when a maximum size isn’t specified. It also allows
					 * matching the width/height of the reference element.
					 */
					size({
						apply: ({ availableHeight, elements }) => {
							const tooltipWrapperStyles = getComputedStyle(tooltipWrapperElement);
							let tooltipHeaderRect: | DOMRect | undefined;
							let tooltipWrapperHeight = availableHeight;

							if (this.title) {
								tooltipHeaderRect = this.tooltipHeaderElement?.getBoundingClientRect();
							}

							let tooltipHeaderHeight = (
								tooltipHeaderRect?.height
								|| 0
							);
							const tooltipHeaderStyles = (
								this.tooltipHeaderElement
									? getComputedStyle(this.tooltipHeaderElement)
									: {} as CSSStyleDeclaration
							);

							if (
								tooltipWrapperStyles.paddingTop
								|| tooltipWrapperStyles.paddingBottom
							) {
								tooltipWrapperHeight -= parseFloat(tooltipWrapperStyles.paddingTop);
								tooltipWrapperHeight -= parseFloat(tooltipWrapperStyles.paddingBottom);
							}

							if (
								this.tooltipHeaderElement
								&& this.hasHeader
								&& (
									tooltipHeaderStyles.paddingTop
									|| tooltipHeaderStyles.paddingBottom
									|| tooltipHeaderStyles.marginTop
									|| tooltipHeaderStyles.marginBottom
								)
							) {
								tooltipHeaderHeight += parseFloat(tooltipHeaderStyles.paddingTop);
								tooltipHeaderHeight += parseFloat(tooltipHeaderStyles.paddingBottom);
								tooltipHeaderHeight += parseFloat(tooltipHeaderStyles.marginTop);
								tooltipHeaderHeight += parseFloat(tooltipHeaderStyles.marginBottom);
							}

							elements.floating.style.maxHeight = `${availableHeight}px`;
							elements.floating.style.setProperty(
								'--tooltip-component-body-max-height',
								`${Math.ceil(tooltipWrapperHeight - tooltipHeaderHeight)}px`,
							);
						},
					}),
					(
						this.tooltipArrowElement
						/**
						 * Provides positioning data for an arrow element (triangle or caret)
						 * inside the floating element, such that it appears to be pointing
						 * toward the center of the reference element.
						 * This is useful to add an additional visual cue to the floating
						 * element about which element it is referring to.
						 */
						&& arrowMiddleware({
							element: this.tooltipArrowElement,
						})
					),
				].filter(Boolean),
			},
		);
		Object.assign(
			this.tooltipPositionerStyles,
			{
				left: `${x}px`,
				top: `${y}px`,
			},
		);
		this.manualTooltipContainerClasses.invisible = false;

		if (
			this.tooltipArrowElement
			&& middlewareData.arrow
		) {
			const {
				x: arrowX,
				y: arrowY,
			} = middlewareData.arrow;
			Object.assign(
				this.tooltipArrowElement.style,
				{
					left: (
						arrowX !== null
							? `${arrowX}px`
							: ''
					),
					top: (
						arrowY !== null
							? `${arrowY}px`
							: ''
					),
				},
			);
			this.tooltipArrowElement.setAttribute(
				'data-side',
				placement.split('-')[0],
			);
		}
	}

	protected onClickOutside(): void {
		this.$emit('click:outside');
	}

	@Public()
	public onCloseClick(
		event?: ServiceEvent,
		originalEvent?: Event,
	): Decorated<void> {
		if (!event) {
			event = new ServiceEvent({
				type: 'close',
			});
		}

		if (typeof originalEvent !== 'undefined') {
			event.payload = originalEvent;
		}

		if (this.beforeClose) {
			this.beforeClose(event);
		}

		if (!event.defaultPrevented) {
			this.$emit('close');
		}
	}

	protected onTooltipModalClick(event: TouchEvent): void {
		const target = event.target as HTMLElement;

		if (
			!this.$currentInstance.isUnmounted
			&& this.tooltipPositionerElement
			&& (
				(
					this.hasCloseButton
					|| this.closeOnModalClick
				)
				&& !this.tooltipPositionerElement.isSameNode(target)
				&& !this.tooltipPositionerElement.contains(target)
			)
			&& !event.defaultPrevented
		) {
			event.preventDefault();
			const closeEvent = new ServiceEvent({
				type: 'close',
				payload: false,
			});
			this.$emit(
				'close',
				closeEvent,
			);
		}
	}

	protected onTooltipPositionerClick(event: MouseEvent): void {
		event.preventDefault();
	}

	protected onVisibilityChange(isVisible: boolean): void {
		this.isVisible = isVisible;
	}
}

export default toNative(TooltipComponent);
